import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { History, Location } from 'history';
import { object as PropObject } from 'prop-types';

import { LoggableError, ValidationError } from '../gs-core/common';

import {
  Doc,
  DocUploadMap,
  DocUploadMapModel,
  DocUpload,
  DocUploadSteps,
  hydrateOwningUnitOrgs,
  hydrateSubjectOrgs,
  MAX_BATCH_FILE_COUNT,
  PermissionEvaluator,
} from '../common/models';
import { DocUploadPanel } from './DocUploadPanel/DocUploadPanel';
import { DocUploadHeader } from './DocUploadHeader/DocUploadHeader';
import { ServiceRegistry } from '../services';

interface DocUploaderViewProps {
  onLoading: (loading: boolean) => void;
  history: History;
  location: Location;
}

interface DocUploaderViewState {
  docUploadMap?: DocUploadMap;
}

export class DocUploaderView extends React.Component<DocUploaderViewProps, DocUploaderViewState> {
  static contextTypes = { services: PropObject };

  constructor(props: DocUploaderViewProps) {
    super(props);
    this.state = {};
  }

  async componentDidMount() {
    const { currentUserService, errorLogger, prmService, docService, docUploadService } = this.context
      .services as ServiceRegistry;

    this.props.onLoading(false);

    try {
      const currentUser = await currentUserService.find();
      const permissions = await currentUserService.findPermissions();

      if (!currentUser) {
        throw new Error('Unable to find current user');
      }

      if (!this.props.location.state) {
        return this.props.history.replace('/');
      }

      const docUploads: DocUpload[] = this.props.location.state.docUploads;

      if (docUploads.length > MAX_BATCH_FILE_COUNT) {
        throw new ValidationError(`Please limit batch uploads to ${MAX_BATCH_FILE_COUNT} files or less.`);
      }

      const subject = await hydrateSubjectOrgs(this.props.location.state.subject, prmService);
      let owningUnit = await hydrateOwningUnitOrgs(this.props.location.state.owningUnit, prmService);

      if (subject) {
        owningUnit = PermissionEvaluator.forUser(currentUser)
          .withPermissions(permissions)
          .forDoc({ subject })
          .findOwningUnitsOverlap()[0];
      }

      const docUploadMap = DocUploadMapModel.create(docUploads);

      this.setState({ docUploadMap });

      await docUploadService.doUploads(docUploadMap, this.changeMap, subject, owningUnit);

      // Flatten and log one error to the server
      const docUploadsWithErrors = docUploadMap.flatten().filter((docUpload) => !!docUpload.error);

      if (docUploadsWithErrors.length) {
        const messages = docUploadsWithErrors.map((docUpload) => {
          const error = docUpload.error as ValidationError;
          const errorMessage = error.messages && error.messages.length ? error.messages.join(', ') : error.message;
          return `Error uploading ${docUpload.file.name} - ${errorMessage || 'Server Error'}`;
        });
        throw LoggableError.create(new ValidationError(messages));
      }

      // Set the batch as the current id cache so we walk over each doc in order
      const ids = docUploads.map((docUpload) => (docUpload.doc as Doc).id);
      docService.overrideCachedIds(ids);

      const firstDoc = docUploads[0].doc as Doc;
      this.props.history.replace(firstDoc.getRoute());
    } catch (err) {
      await errorLogger.log(err);
    }
  }

  changeMap = (docUploadMap: DocUploadMap) => this.setState({ docUploadMap });

  render() {
    const { docUploadMap } = this.state;

    if (!docUploadMap) {
      return null;
    }

    return (
      <div className="DocUploaderView">
        <DocUploadHeader docUploadMap={docUploadMap} />
        {DocUploadSteps.DISPLAY_ORDER.map((step) =>
          docUploadMap.byStep(step).map((docUpload) => (
            <Row key={`docUpload-${docUpload.id}`}>
              <Col xs={12}>
                <DocUploadPanel docUpload={docUpload} />
              </Col>
            </Row>
          ))
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/DocUploaderView/DocUploaderView.tsx